import { HTML5toTouch } from "rdndmb-html5-to-touch";
import { useEffect, useState } from "react";
import { DndProvider } from "react-dnd-multi-backend";
import "../App.css";
import { DraggableMessage } from "../components/Draggables";
import {
  COLOR_HIGHLIGHTED,
  FONT,
  REGEX_VALID_DECIMAL,
  ROMAN_FONT,
  ROMAN_SYSTEM
} from "../defaults";
import { convertToAlternate, convertToDecimal, increaseProgress } from "../helpers";
import coin_C from "../images/coin_C.png";
import coin_D from "../images/coin_D.png";
import coin_I from "../images/coin_I.png";
import coin_L from "../images/coin_L.png";
import coin_M from "../images/coin_M.png";
import coin_V from "../images/coin_V.png";
import coin_X from "../images/coin_X.png";
import {
  buttonCheck,
  level0RomanTask1,
  level0RomanTask2,
  levelCorrect,
  levelIncorrect,
  levelNoInput
} from "../strings";

const LevelNumber = 0;

function generateNumber(currentProgress) {
  const numbers = [8, 43, 122];
  if (currentProgress >= numbers.length) {
    return Math.ceil(Math.random() * 998) + 1; // Generate integer between 1 and 999. These are optional but slightly harder, because the numbers are larger.
  }
  return numbers[currentProgress];
}

const containerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
  fontFamily: FONT,
  fontSize: "3dvh",
  padding: "0",
  borderRadius: "0.25em",
  width: "6dvh",
  margin: "0 auto",
  justifyContent: "center",
};

const containerArrayStyle = {
  display: "flex",
  flexDirection: "row",
  flexBasis: "0",
  flexWrap: "wrap",
  rowGap: "5dvh",
  justifyContent: "center",
  position: "absolute",
  top: "40%",
  transform: "translateY(-20%)",
  width: "100%",
  textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
  fontFamily: FONT,
  borderRadius: "0.25em",
}

const inputStyle = {
  fontFamily: FONT,
};


const headerStyle = {
  marginTop: "4dvh",
  padding: "0 3dvh",
  fontFamily: FONT,
  fontSize: "4dvh",
  textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
};


/**
 * Returns HTML for a single container. Depending on isLast, sometimes returns plus-containers or equal-sign-containers and input field.
 * @param {String} romanChar The Roman number letter which will be displayed as a coin and as decimal.
 * @param {boolean} isLast Whether this is the last/rightmost container being rendered, in which case no plus will be added, and instead an equal sign and input field are added.
 */
function renderContainers(romanChar, isLast, input, setInput, disableInput) {
  let coinImage = "";
  switch (romanChar) {
    case 'M':
      coinImage=coin_M;
      break;
    case 'D':
      coinImage=coin_D;
      break;
    case 'C':
      coinImage=coin_C;
      break;
    case 'L':
      coinImage=coin_L;
      break;
    case 'X':
      coinImage=coin_X;
      break;
    case 'V':
      coinImage=coin_V;
      break;
    case 'I':
      coinImage=coin_I;
      break;
    default:
      console.log("Error renderContainers(): Invalid romanChar passed as argument!")
  }
  let coin = <span style={{width: "7.5dvh", height: "7.5dvh"}}><img src={coinImage} alt="coin" style={{ width: "100%", height: "100%", filter: "drop-shadow(0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5))",}}/></span>; // Use image instead of coin object.
  let decimal = convertToDecimal(ROMAN_SYSTEM, romanChar);
  return (
    <div style={{display: "flex", flexDirection: "row"}}>
      <div style={containerStyle}>
        {coin}
        <span style={{fontSize: "6dvh", fontFamily: ROMAN_FONT, fontWeight: "bold"}}>&#11107;</span>
        {decimal}
      </div>
      {isLast ? "" : 
        <div style={containerStyle}>
          <span style={{visibility: "hidden", width: "7dvh", height: "7dvh"}}>{coin}</span>
          <span style={{fontSize: "6dvh", fontFamily: ROMAN_FONT, fontWeight: "bold", visibility: "hidden"}}>&#11107;</span>
          <span style={{marginTop: "0.5dvh"}}>+</span>
        </div>}
      {!isLast ? "" : 
        <div style={containerStyle}>
          <span style={{visibility: "hidden", width: "7dvh", height: "7dvh"}}>{coin}</span>
          <span style={{fontSize: "6dvh", fontFamily: ROMAN_FONT, fontWeight: "bold", visibility: "hidden"}}>&#11107;</span>
          <span style={{marginTop: "0.5dvh"}}>=</span>
        </div>}
      {!isLast ? "" : 
        <div style={{display: "flex", flexDirection: "column", maxWidth: "20dvh", margin: "0 auto"}}>
          <span style={{visibility: "hidden", width: "7dvh", height: "7dvh"}}>{coin}</span>
          <span style={{fontSize: "6dvh", fontFamily: ROMAN_FONT, fontWeight: "bold", visibility: "hidden"}}>&#11107;</span>
          <span style={{marginTop: "0.1vh"}}>
            <input
            className="input-field"
            style={inputStyle}
            type="text"
            inputMode="numeric"
            value={input}
            maxLength={5}
            disabled={disableInput}
            placeholder=""
            pattern={REGEX_VALID_DECIMAL}
            onChange={(e) => {
              e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
              setInput(e.target.value)}
            }
            />
          </span>
          
        </div>
      }
    </div>
  );
}

/**
 * Iterates through Roman string and renders all containers.
 * @param {number} number 
 * @param {String} input 
 * @param {*} setInput 
 * @param {*} checkAnswers 
 * @param {boolean} disableInput 
 * @returns 
 */
function iterateString(number, input, setInput, disableInput, buttonText) {
  let romanString = convertToAlternate(ROMAN_SYSTEM, number);
  let containerArray = [];
  let isLast = false;
  for (let i = 0; i < romanString.length; ++i) {
    (i === (romanString.length - 1)) ? isLast = true : isLast = false; // Flag checking whether currently processing last Roman char.
    containerArray.push(renderContainers(romanString[i], isLast, input, setInput, disableInput, buttonText)); // Create individual containers and put them in an array.
  }
  return <div style={containerArrayStyle}>{containerArray}</div>; // Render the array.
}

function Level0Roman({levelProgress, setLevelProgress, showMessage, setShowMessage, disableInput, setDisableInput}) {
  const [number, setNumber] = useState(
    generateNumber(levelProgress[LevelNumber])
  );
  const [input, setInput] = useState("");
  const [message, setMessage] = useState("");
  const [correct, setCorrect] = useState(false);

  useEffect(() => {
    setNumber(generateNumber(levelProgress[LevelNumber]));
    setInput("");
  }, [levelProgress]);

  const checkAnswers = () => {
    const isCorrect = (input === number.toString());
    setCorrect(isCorrect);
    if (isCorrect) {
      setMessage(levelCorrect)
    } else if (input === "") {
      setMessage(levelNoInput);
    } else {
      setMessage(levelIncorrect);
    }
    displayMessage();
  };

  const displayMessage = () => {
    setDisableInput(true);
    setShowMessage(true);
  };

  const handleCloseMessage = () => {
    if (correct) {
      increaseProgress(levelProgress, setLevelProgress, LevelNumber, ROMAN_SYSTEM);
      setInput("");
    }
    if (input !== "") {
      setInput("");
    }
    setMessage("");
    setDisableInput(false);
    setCorrect(false);
  };

  return (
    <div>
      <div style={headerStyle}>
        {level0RomanTask1} <span style={{fontFamily: ROMAN_FONT, color: COLOR_HIGHLIGHTED, fontSize: "5dvh"}}>{convertToAlternate(ROMAN_SYSTEM, number)}</span> {level0RomanTask2}
      </div>
      <DndProvider options={HTML5toTouch}>
        {showMessage && <DraggableMessage show={showMessage} setShow={setShowMessage} handleClose={handleCloseMessage} message={message}/>} {/* Conditionally rendering it with showMessage makes the message re-render, and hence center itself, every time. */}
        <div>
          {iterateString(number, input, setInput, disableInput)}
        </div>
      </DndProvider>
      <button className="checkAnswer-button" onClick={checkAnswers} disabled={disableInput}>
          {buttonCheck}
      </button>
    </div>
  );
}

export default Level0Roman;
